import React from 'react';
import { propertyTypes, loanPurpose, keys } from '@enums';
import { SCENARIO_PREFETCH_DELAY, BASE_SCENARIO } from '@constants';
import tracker from '@utils/tracker';
import {
  isPlainObject,
  isFunction,
  isNumber,
  isString,
  isEqual,
  sortBy,
  lowerFirst
} from 'lodash';
export {
  isMobile as isMobileDevice,
  isDesktop as isDesktopDevice
} from './device';

export function getState(state, value, name) {
  if (!isPlainObject(state) || (!value && !name)) {
    return state;
  }

  if (isFunction(value)) {
    return value(state);
  }

  if (isPlainObject(value)) {
    return { ...state, ...value };
  }

  if (!name) {
    return state;
  }

  if (Array.isArray(value)) {
    return { ...state, [name]: [...value] };
  }

  return { ...state, [name]: value };
}

let config = null;

export function getConfig() {
  return config;
}

export function initProps(props) {
  props = camelCaseProps(props);

  if (props.contactFormHtml) {
    const html = props.contactFormHtml
      .trim()
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    props.contactFormHtml = decodeHtml(html);
  }

  if (!isPlainObject(props.validations)) {
    props.validations = {};
  }

  Object.keys(props.validations).forEach(key => {
    if (props.validations[key] === null && key.match(/^(min|max)/)) {
      props.validations[key] = 0;
    }
  });

  if (config === null) {
    config = Object.assign({}, props.configuration);

    Object.keys(config).forEach(key => {
      if (isString(config[key])) {
        config[key] = config[key].trim();
      }
    });

    config.scenarioPrefetchDelay ??= SCENARIO_PREFETCH_DELAY;
  }

  const { defaultLoanPurpose, purchase, refinance } = props.defaultScenarios;

  [keys.LOAN_AMOUNT, keys.HOME_VALUE, keys.ZIP_CODE].forEach(key => {
    if (purchase[key]) {
      purchase[key] = String(purchase[key]);
    }

    if (refinance[key]) {
      refinance[key] = String(refinance[key]);
    }
  });

  props.defaultScenarios = {
    defaultLoanPurpose: isValidLoanPurpose(defaultLoanPurpose)
      ? defaultLoanPurpose
      : BASE_SCENARIO.loanPurpose,
    purchase: Object.assign({}, BASE_SCENARIO, purchase, {
      loanPurpose: loanPurpose.PURCHASE
    }),
    refinance: Object.assign({}, BASE_SCENARIO, refinance, {
      loanPurpose: loanPurpose.REFINANCE
    })
  };

  props.initialScenario =
    props.defaultScenarios[props.defaultScenarios.defaultLoanPurpose];

  const badgesArr = Array.isArray(props.badges) ? props.badges : [];
  const badges = badgesArr.reduce((obj, badge) => {
    if (badge?.badgeName) {
      if (badge.tooltipText) {
        badge.tooltipText = badge.tooltipText.trim();
      }

      if (badge.badgeStyle) {
        badge.badgeStyle = badge.badgeStyle.toLowerCase().trim();
      }

      obj[badge.badgeName] = badge;
    }

    return obj;
  }, {});

  props.badges = badges;
  config.badges = badges;

  tracker.isDebug = config.enableDebugMode;

  return props;
}

export function camelCaseProps(props) {
  if (!props) {
    return props;
  }

  if (isPlainObject(props)) {
    const newProps = {};

    Object.keys(props).forEach(key => {
      const newKey = lowerFirst(key);
      const value = props[key];

      // If props are an array or an object
      // recursively map those keys as well
      const newValue =
        typeof value === 'object' ? camelCaseProps(value) : value;

      newProps[newKey] = newValue;
    });

    return newProps;
  }

  if (Array.isArray(props)) {
    return props.map(camelCaseProps);
  }

  return props;
}

export function isValidLoanType(type) {
  if (!isString(type)) {
    return false;
  }

  return isFixedRate(type) || isAdjustableRate(type);
}

export function isAdjustableRate(type) {
  if (!isString(type)) {
    return false;
  }

  return type.toLowerCase().trim() === 'adjustable';
}

export function isFixedRate(type) {
  if (!isString(type)) {
    return false;
  }

  return type.toLowerCase().trim() === 'fixed';
}

export function isPurchase(type) {
  if (!isString(type)) {
    return false;
  }

  return type.toLowerCase().trim() === 'purchase';
}

export function isRefinance(type) {
  if (!isString(type)) {
    return false;
  }

  return type.toLowerCase().trim() === 'refinance';
}

export function isValidLoanPurpose(type) {
  return isPurchase(type) || isRefinance(type);
}

export function formatCurrency(num, showDecimals) {
  if (isString(num)) {
    num = num.replace(/[$,]/g, '');
  }

  const isNegative = num < 0;
  const decimals = showDecimals ? 2 : 0;

  num = Math.abs(Number(num)).toLocaleString('en-US', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals
  });

  return isNegative ? `-$${num}` : `$${num}`;
}

export function calculatePercent(num1, num2, addDecimals) {
  let num = !isNumber(num2 || isNaN(Number(num2)))
    ? num1
    : Math.round((parseFloat(num1) / parseFloat(num2)) * 100);

  return formatPercent(num, addDecimals);
}

export function formatPercent(num, addDecimals = true, addPercentSign = true) {
  if (addDecimals) {
    num = Number(num).toLocaleString('en-US', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    });
  }

  return num + (addPercentSign ? '%' : '');
}

export function formatPhoneNumber(num, showIntlCode) {
  if (!num) {
    return null;
  }

  const cleaned = String(num).replace(/\D/g, '');

  if (cleaned.length !== 10 && cleaned.length !== 11) {
    return null;
  }

  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    const intlCode = showIntlCode && match[1] ? '+1 ' : '';
    return `${intlCode}(${match[2]}) ${match[3]}-${match[4]}`;
  }

  return null;
}

const emailRegex =
  /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

export function isValidEmail(email, maxLength = 320) {
  if (!isString(email) || email.length > maxLength) {
    return false;
  }

  const emailParts = email.split('@');

  if (emailParts.length !== 2) return false;

  const [account, address] = emailParts;

  if (account.length > 64 || address.length > 255) {
    return false;
  }

  if (address.split('.').some(part => part.length > 63)) {
    return false;
  }

  return emailRegex.test(email);
}

const phoneRegex = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/;

export function isValidPhone(phone) {
  if (!isString(phone) || !phone) return false;
  return phoneRegex.test(phone);
}

export function isVowel(letter) {
  if (!isString(letter)) {
    return false;
  }

  return ['a', 'e', 'i', 'o', 'u'].includes(letter[0].toLowerCase());
}

export function isSSR() {
  return typeof window === 'undefined' || typeof document === 'undefined';
}

export function compareObjects(a, b, keys) {
  if ((!a && b) || (a && !b)) {
    return false;
  }

  if (!keys) {
    return isEqual(a, b);
  }

  keys = [keys].flat();

  const getObj = obj => {
    const newObj = {};
    keys.forEach(key => (newObj[key] = obj[key]));
    return newObj;
  };

  return isEqual(getObj(a), getObj(b));
}

export function compareLoanDetails(a, b, keys) {
  if ((!a && b) || (a && !b)) {
    return false;
  }

  const getCashOut = value => {
    value = String(value).trim();

    if (!value || value === '0' || isNaN(value)) {
      value = '';
    } else {
      const num = parseFloat(value);

      if (!num || isNaN(num) || num < 0) {
        value = '';
      }
    }

    return value;
  };

  a = {
    ...a,
    loanAmount: String(a.loanAmount),
    homeValue: String(a.homeValue),
    cashOutAmount: getCashOut(a.cashOutAmount)
  };

  b = {
    ...b,
    loanAmount: String(b.loanAmount),
    homeValue: String(b.homeValue),
    cashOutAmount: getCashOut(b.cashOutAmount)
  };

  return compareObjects(a, b, keys);
}

export function compareArrays(a, b, checkOrder) {
  if (!Array.isArray(a) || !Array.isArray(b)) {
    console.error('Arguments must be arrays');
    return false;
  }

  if (a.length !== b.length) {
    return false;
  }

  if (!checkOrder) {
    a = sortBy(a);
    b = sortBy(b);
  }

  return isEqual(a, b);
}

// We cache the results of requests to avoid
// redundant API calls. This function generates an
// ID for a specific loan scenario, and is used
// to identify a particular set of loan details
// Original hash function: https://stackoverflow.com/a/52171480
export function generateId(obj, seed = 0) {
  const str = Object.values(obj)
    .map(v => String(v).trim())
    .join('')
    .toLowerCase()
    .replace(/[^a-z0-9]/gi, '');

  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;

  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }

  h1 =
    Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
    Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 =
    Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
    Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return String(4294967296 * (2097151 & h2) + (h1 >>> 0));
}

// Gets array of elements that should be translated
// down when the calculator inputs are open. We use
// a closure to avoid querying elements multiple times
const getElementsToTransform = (() => {
  let elements = null;

  return () => {
    if (elements?.length || isSSR()) {
      return elements ?? [];
    }

    let sections = Array.from(
      document.querySelectorAll('#main > div, #main > section')
    );

    // Only transform sections after the calculator
    const index = sections.findIndex(
      s => s.id === 'mortgage-rate-calculator-wrapper'
    );

    if (index > -1) {
      sections = sections.slice(index + 1);
    }

    elements = sections
      .concat(document.querySelector('#footer'))
      .filter(s => s && s.id !== 'mortgage-rate-calculator-form');

    return elements;
  };
})();

export { getElementsToTransform };

export function getPropertyTypeText(type) {
  const key = Object.keys(propertyTypes).find(p => {
    return propertyTypes[p].value === type;
  });

  const data = propertyTypes[key];

  return (data?.loanDetailsText ?? data?.label ?? 'home').toLowerCase();
}

export function isValidDate(date) {
  if (!date) {
    return false;
  }

  date = new Date(date);

  return date instanceof Date && !isNaN(date);
}

export function getEffectiveDate(date) {
  if (isString(date) && date.includes('/Date')) {
    date = new Date(parseInt(date.substr(6)));
  }

  date = new Date(date);

  return isValidDate(date) ? new Date(date) : new Date();
}

export function decodeHtml(html) {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

export function filterRequestPhaseData(loanDetails, requestPhases) {
  requestPhases = JSON.parse(JSON.stringify(requestPhases));

  return requestPhases
    .map(data => {
      const requestPhase = { ...data };

      requestPhase.products = requestPhase.products.filter(
        ({ productRestriction }) => {
          if (!productRestriction) {
            return true;
          }

          const {
            minLoanAmount,
            maxLoanAmount,
            minCreditScore,
            maxCreditScore
          } = productRestriction;

          const creditScore = parseInt(loanDetails.creditScore);

          let loanAmount = parseFloat(loanDetails.loanAmount);
          const cashOutAmount = parseFloat(loanDetails.cashOutAmount);

          if (
            isRefinance(loanDetails.loanPurpose) &&
            !isNaN(cashOutAmount) &&
            cashOutAmount > 0
          ) {
            loanAmount += cashOutAmount;
          }

          if (
            (minLoanAmount > 0 && loanAmount < minLoanAmount) ||
            (maxLoanAmount > 0 && loanAmount > maxLoanAmount) ||
            (minCreditScore > 0 && creditScore < minCreditScore) ||
            (maxCreditScore > 0 && creditScore > maxCreditScore)
          ) {
            return false;
          }

          return true;
        }
      );

      return requestPhase.products.length ? requestPhase : null;
    })
    .filter(r => r);
}

export function paragraphToNewline(text) {
  if (!text) {
    return '';
  }

  return !isString(text)
    ? text
    : text
        .replace(/<br\s?\/?>/gm, '\n')
        .replace(/<\s*p[^>]*>([^<]*)<\s*\/\s*p\s*>/gm, '$1\n')
        .trim()
        .replace(/[\r\n]+/g, '\n');
}

export function formatParagraphs(text, encode = true) {
  return paragraphToNewline(text)
    .split('\n')
    .map((p, index) => {
      const text = p.trim();

      return encode ? (
        <p key={index}>{text}</p>
      ) : (
        <p key={index} dangerouslySetInnerHTML={{ __html: text }} />
      );
    });
}

export function isInPage(node) {
  if (isSSR() || !node) {
    return false;
  }

  return !document.body.contains || document.body.contains(node);
}
